import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { optionsPartido, optionsTipoEntidad } from "../datos/options.js";
import { AuthContext } from "../context/authContext.jsx";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ.js";
import TileWMS from "ol/source/TileWMS.js";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Cluster from "ol/source/Cluster";
import GeoJSON from "ol/format/GeoJSON";
import { Select } from "ol/interaction";
import Pointer from "ol/interaction/Pointer.js";
import { Style, Fill, Stroke, Circle, Text } from "ol/style";
import { useGeographic } from "ol/proj";
import { defaults as defaultControls, Zoom } from "ol/control.js";
import "ol/ol.css";

// Iconos inline SVG (reemplazo de lucide-react)
const MapIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
        <line x1="9" y1="3" x2="9" y2="18"/>
        <line x1="15" y1="6" x2="15" y2="21"/>
    </svg>
);

const ToolsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
);

const LayersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
    </svg>
);

const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
);

// Extent que abarca todas las features de un clúster (evita zoom excesivo al usar
// el punto del clúster, cuyo extent es mínimo).
const getClusterExtent = (features) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    features.forEach((f) => {
        const geom = f.getGeometry();
        if (!geom) return;
        const ext = geom.getExtent();
        minX = Math.min(minX, ext[0]);
        minY = Math.min(minY, ext[1]);
        maxX = Math.max(maxX, ext[2]);
        maxY = Math.max(maxY, ext[3]);
    });
    if (!Number.isFinite(minX)) return null;
    return [minX, minY, maxX, maxY];
};

const buildOsmSelection = (p) => ({
    id: p.id,
    osm_id: p.osm_id,
    osm_type: p.osm_type,
    nombre_completo: p.nombre_completo,
    tipo_entidad: p.tipo_entidad,
    municipio: p.municipio,
    localidad: p.localidad,
    codigo_partido: p.codigo_partido,
    partido_nombre: p.partido_nombre,
    domicilio: p.domicilio,
    telefono: p.telefono,
    email: p.email,
    web: p.web,
    deportes: p.deportes,
    leisure: p.leisure,
    amenity: p.amenity,
    estado: p.estado,
    entidad_id_red: p.entidad_id_red,
    observaciones: p.observaciones
});

export const MapaEntidades = ({ mode = 'public', zoomTo, className = '', filterByMunicipio = null }) => {
    useGeographic();
    
    const isPrivate = mode === 'private';
    
    // Obtener rol y entidadId del usuario autenticado si es privado
    const auth = useContext(AuthContext);
    const { role, entidadId, userMunicipio, isEspecial, token } = isPrivate && auth ? auth : { role: null, entidadId: null, userMunicipio: null, isEspecial: false, token: null };
    const isEditorEntidad = isPrivate && role === 'editor_entidad';
    const isVisualizador = isPrivate && role === 'visualizador_municipio';
    // Capa OSM (admin, provincial especial o municipal por partido)
    const canViewOsmStaging = isPrivate && (role === 'admin' || (role === 'editor_provincial' && isEspecial === true) || (role === 'editor_municipio' && !!userMunicipio));
    
    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const [entidades, setEntidades] = useState([]);
    const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
    const [entidadDetalle, setEntidadDetalle] = useState(null); // Datos extendidos de la API (privado)
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [selectedEntidadId, setSelectedEntidadId] = useState(null);
    const [loadingZoomEntidad, setLoadingZoomEntidad] = useState(false);
    const vectorSource = useRef(new VectorSource());
    const sedeSource = useRef(new VectorSource());
    const clusterSource = useRef(null);
    const parcelasSource = useRef(new VectorSource());
    const osmStagingSource = useRef(new VectorSource());
    const municipiosSource = useRef(new VectorSource());
    const sedeLayerRef = useRef(null);
    const parcelasLayerRef = useRef(null);
    const osmStagingLayerRef = useRef(null);
    const municipiosLayerRef = useRef(null);
    const partidoLayerRef = useRef(null);
    const highlightLayerRef = useRef(null);
    const highlightSourceRef = useRef(new VectorSource());
    
    // Extraer codigoPartido del entidadId (formato "060-012")
    const getCodigoPartidoFromEntidadId = (id) => {
        if (!id) return null;
        const parts = id.split('-');
        return parts[0] || null;
    };
    
    const codigoPartidoUsuario = isEditorEntidad ? getCodigoPartidoFromEntidadId(entidadId) : null;
    
    // Estado para buscador
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearch, setShowSearch] = useState(isPrivate ? !zoomTo : false);

    // Función de búsqueda (local + remota para entidades sin sede)
    const handleSearch = async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        const normalizedQuery = query.toLowerCase().trim();
        const localResults = [];

        // Buscar en sedes (source original, no cluster)
        if (sedeSource.current) {
            sedeSource.current.getFeatures().forEach(feature => {
                const idRed = feature.get('id_red') || '';
                const nombreCompleto = feature.get('nombre_completo') || '';
                const nombreFantasia = feature.get('nombre_fantasia') || '';
                
                if (idRed.toLowerCase().includes(normalizedQuery) ||
                    nombreCompleto.toLowerCase().includes(normalizedQuery) ||
                    nombreFantasia.toLowerCase().includes(normalizedQuery)) {
                    localResults.push({
                        feature: feature,
                        idRed: idRed,
                        nombre: nombreCompleto || nombreFantasia,
                        type: 'sede',
                        mapeable: true
                    });
                }
            });
        }

        // Buscar en parcelas
        if (parcelasSource.current) {
            parcelasSource.current.getFeatures().forEach(feature => {
                const idRed = feature.get('id_red') || '';
                const nombreCompleto = feature.get('nombre_completo') || '';
                const nombreFantasia = feature.get('nombre_fantasia') || '';
                
                if (idRed.toLowerCase().includes(normalizedQuery) ||
                    nombreCompleto.toLowerCase().includes(normalizedQuery) ||
                    nombreFantasia.toLowerCase().includes(normalizedQuery)) {
                    localResults.push({
                        feature: feature,
                        idRed: idRed,
                        nombre: nombreCompleto || nombreFantasia,
                        type: 'parcela',
                        mapeable: true
                    });
                }
            });
        }

        // Buscar en backend (entidades sin sede que no están en el mapa)
        try {
            const response = await fetch(`/api/entidades/buscar?q=${encodeURIComponent(query.trim())}`);
            if (response.ok) {
                const data = await response.json();
                const localIds = new Set(localResults.map(r => r.idRed));
                
                data.results.forEach(ent => {
                    if (!localIds.has(ent.id_red)) {
                        localResults.push({
                            idRed: ent.id_red,
                            nombre: ent.nombre_completo || ent.nombre_fantasia,
                            nombre_fantasia: ent.nombre_fantasia,
                            tipo_entidad: ent.tipo_entidad,
                            municipio: ent.municipio,
                            sin_sede: ent.sin_sede,
                            tiene_sede: ent.tiene_sede,
                            type: 'remota',
                            mapeable: false
                        });
                    }
                });
            }
        } catch (error) {
            console.error('Error buscando entidades en backend:', error);
        }

        setSearchResults(localResults);
    };

    // Zoom a entidad encontrada
    const zoomToSearchResult = (result) => {
        if (!map || !result) return;

        if (!result.mapeable) {
            setInfoMessage(`"${result.nombre}" no tiene sede georreferenciada. No es posible ubicarla en el mapa.`);
            return;
        }

        const geometry = result.feature.getGeometry();
        if (!geometry) return;

        const extent = geometry.getExtent();
        
        // Resaltar la entidad
        highlightSourceRef.current.clear();
        highlightSourceRef.current.addFeature(result.feature);
        
        // Zoom a la entidad
        map.getView().fit(extent, {
            padding: [100, 100, 100, 100],
            duration: 1000,
            maxZoom: 18
        });

        // Establecer entidad seleccionada para mostrar botón de restablecer
        setSelectedEntidadId(result.idRed || result.id);

        // Cerrar resultados
        setSearchResults([]);
        setSearchQuery('');
    };
    
    // Función simple para limpiar búsqueda (solo para el botón X del input)
    const clearSearch = () => {
        setSearchResults([]);
        setSearchQuery('');
        setSelectedEntidadId(null);
        highlightSourceRef.current.clear();
    };

    // Función para limpiar búsqueda y volver al zoom original
    const clearSearchAndResetZoom = () => {
        setSearchResults([]);
        setSearchQuery('');
        setSelectedEntidadId(null);
        
        if (highlightSourceRef.current) {
            highlightSourceRef.current.clear();
        }

        if (!map) return;
        
        if (isPrivate) {
            if (selectedPartido) {
                const fetchPartidoAndZoom = async () => {
                    try {
                        const response = await fetch(`/api/partido_geom/${selectedPartido}`);
                        if (response.ok) {
                            const geojsonData = await response.json();
                            if (geojsonData?.features?.length > 0) {
                                const sourcePartido = new VectorSource({
                                    features: new GeoJSON().readFeatures(geojsonData, {
                                        featureProjection: 'EPSG:4326',
                                        dataProjection: 'EPSG:4326'
                                    })
                                });
                                map.getView().fit(sourcePartido.getExtent(), {
                                    padding: [50, 50, 50, 50],
                                    duration: 1000,
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error resetting zoom to partido:', error);
                    }
                };
                fetchPartidoAndZoom();
            } else {
                map.getView().animate({
                    center: [-58.0, -37.5],
                    zoom: 7,
                    duration: 1000
                });
            }
        } else {
            map.getView().setCenter([-60.50, -37.16]);
            map.getView().setZoom(7);
        }
    };
    
    // Mensaje informativo tipo toast
    const [infoMessage, setInfoMessage] = useState('');

    // Auto-limpiar mensaje después de 5 segundos
    useEffect(() => {
        if (infoMessage) {
            const timer = setTimeout(() => setInfoMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [infoMessage]);

    // Estado para controlar paneles
    const [showTools, setShowTools] = useState(false);
    const [showLayers, setShowLayers] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [selectedTipoEntidad, setSelectedTipoEntidad] = useState("");
    const [selectedReferente, setSelectedReferente] = useState("");
    
    // Estado para visibilidad de capas
    const [showSedes, setShowSedes] = useState(true);
    const [showParcelas, setShowParcelas] = useState(false);
    const [showMunicipios, setShowMunicipios] = useState(true);
    // Capa OSM sin validar (solo admin / provincial especial)
    const [showOsmStaging, setShowOsmStaging] = useState(false);
    const [osmSeleccionada, setOsmSeleccionada] = useState(null);
    const [osmAccionLoading, setOsmAccionLoading] = useState(false);
    const [osmAccionError, setOsmAccionError] = useState(null);
    const [osmAccionMsg, setOsmAccionMsg] = useState(null);
    const [osmRejectMode, setOsmRejectMode] = useState(false);
    const [osmObservaciones, setOsmObservaciones] = useState('');
    const [osmNombreEditado, setOsmNombreEditado] = useState('');
    const [osmDomicilioEditado, setOsmDomicilioEditado] = useState('');
    const [refreshSedesKey, setRefreshSedesKey] = useState(0);
    
    // Estado para conteo de entidades filtradas
    const [filteredCount, setFilteredCount] = useState(0);
    const [showArba, setShowArba] = useState(false);
    const [baseLayer, setBaseLayer] = useState("osm"); // osm, satellite, topo
    
    // Estado para hover preview
    const [hoveredEntity, setHoveredEntity] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    
    // Estado para filtro de partido
    const [selectedPartido, setSelectedPartido] = useState(() => {
        if (zoomTo && zoomTo.codigo_partido) {
            return zoomTo.codigo_partido.toString().padStart(3, '0');
        }
        if (isPrivate) {
            if (role === 'editor_entidad' && entidadId) {
                const parts = entidadId.split('-');
                return parts[0] || "";
            }
            if (filterByMunicipio) {
                return filterByMunicipio.toString().padStart(3, '0');
            }
        }
        return "";
    });

    // Filtro de partido para la capa OSM (municipal: su partido; resto: partido seleccionado)
    const osmPartidoFiltro = role === 'editor_municipio'
        ? (userMunicipio || '')
        : (selectedPartido || '');
    
    // Estado para modo de estilo
    const [styleMode, setStyleMode] = useState("single"); // "single" | "byType" | "byEditor"

    // Colores por tipo de entidad
    const getColorByTipo = (tipo) => {
        const colors = {
            "club": "#3b82f6",  // azul
            "asociación civil": "#3b82f6",  // azul (con tilde)
            "centro deportivo": "#3b82f6",  // azul
            "sociedad fomento actividad deportiva": "#3b82f6",  // azul 
            "otro": "#6b7280",              // gris
            default: "#6b7280"              // gris por defecto
        };
        return colors[tipo?.toLowerCase()] || colors.default;
    };

    // Estilo para las entidades deportivas
    const entidadStyle = (feature) => {
        const tipo = feature.get("tipo_entidad") || "";
        const nombre = feature.get("nombre_completo") || "Sin nombre";
        const color = styleMode === "byType" ? getColorByTipo(tipo) : "#3b82f6"; // azul por defecto
        
        const currentZoom = map?.getView().getZoom() || 10;
        const showLabel = currentZoom >= 13;
        
        return new Style({
            image: new Circle({
                radius: 10,
                fill: new Fill({ color: color }),
                stroke: new Stroke({ color: "#fff", width: 2 })
            }),
            text: showLabel ? new Text({
                text: String(nombre),
                fill: new Fill({ color: "#1f2937" }),
                stroke: new Stroke({ color: "#fff", width: 3 }),
                font: "12px sans-serif",
                offsetY: -15,
                textAlign: "center"
            }) : null
        });
    };

    // Capas base
    const osmLayer = useRef(new TileLayer({ 
        source: new OSM(), 
        visible: true 
    }));
    const satelliteLayer = useRef(new TileLayer({ 
        source: new XYZ({
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            attributions: 'Esri'
        }), 
        visible: false 
    }));
    const topoLayer = useRef(new TileLayer({ 
        source: new XYZ({
            url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
            attributions: 'OpenTopoMap'
        }), 
        visible: false 
    }));
    
    // IGN Argentina
    const ignLayer = useRef(new TileLayer({
        source: new XYZ({
            url: 'https://www.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/capabaseargenmap@EPSG%3A3857@png/{z}/{x}/{-y}.png',
            attributions: 'IGN Argentina'
        }),
        visible: false
    }));
    
    // CartoDB Positron (light)
    const positronLayer = useRef(new TileLayer({
        source: new XYZ({
            url: 'https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            attributions: 'CartoDB'
        }),
        visible: false
    }));
    
    // CartoDB Voyager (with buildings)
    const voyagerLayer = useRef(new TileLayer({
        source: new XYZ({
            url: 'https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
            attributions: 'CartoDB'
        }),
        visible: false
    }));
    
    // CartoDB Dark
    const darkLayer = useRef(new TileLayer({
        source: new XYZ({
            url: 'https://{a-d}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
            attributions: 'CartoDB'
        }),
        visible: false
    }));
    
    // Ref para capa ARBA
    const arbaLayerRef = useRef(null);

    // Inicializar mapa
    useEffect(() => {
        const vectorLayer = new VectorLayer({
            source: vectorSource.current,
            style: entidadStyle
        });

        // Crear cluster source para agrupar sedes cercanas
        clusterSource.current = new Cluster({
            source: sedeSource.current,
            distance: 40,
            minDistance: 20
        });

        // Estilo para clusters y puntos individuales
        const clusterStyle = (feature) => {
            const features = feature.get('features');
            const size = features ? features.length : 1;
            
            if (size > 1) {
                return new Style({
                    image: new Circle({
                        radius: 15 + Math.min(size * 2, 15),
                        fill: new Fill({
                            color: 'rgba(59, 130, 246, 0.8)'
                        }),
                        stroke: new Stroke({
                            color: '#fff',
                            width: 2
                        })
                    }),
                    text: new Text({
                        text: size.toString(),
                        fill: new Fill({
                            color: '#fff'
                        }),
                        font: 'bold 12px sans-serif',
                        offsetY: 1
                    })
                });
            } else {
                return entidadStyle(feature);
            }
        };

        const sedeLayer = new VectorLayer({ 
            source: clusterSource.current, 
            style: clusterStyle,
            declutter: true,
            zIndex: 10 
        });
        sedeLayerRef.current = sedeLayer;

        const parcelasLayer = new VectorLayer({
            source: parcelasSource.current,
            style: new Style({
                fill: new Fill({ color: 'rgba(66, 153, 225, 0.3)' }),
                stroke: new Stroke({ color: '#2b6cb0', width: 2 })
            }),
            zIndex: 3
        });
        parcelasLayerRef.current = parcelasLayer;

        // Capa de límites de todos los municipios (prendida por defecto)
        const municipiosLayer = new VectorLayer({
            source: municipiosSource.current,
            style: new Style({
                fill: new Fill({ color: 'rgba(100, 116, 139, 0.04)' }),
                stroke: new Stroke({ color: 'rgba(100, 116, 139, 0.7)', width: 1 })
            }),
            visible: true,
            zIndex: 0
        });
        municipiosLayerRef.current = municipiosLayer;

        // Capa de entidades OSM (color según estado de validación)
        const osmStagingLayer = new VectorLayer({
            source: osmStagingSource.current,
            style: (feature) => {
                const estado = feature.get('estado') || 'pendiente';
                const paleta = {
                    pendiente: { fill: 'rgba(249, 115, 22, 0.85)', stroke: '#9a3412' },
                    rechazado: { fill: 'rgba(239, 68, 68, 0.85)', stroke: '#7f1d1d' },
                    importado: { fill: 'rgba(34, 197, 94, 0.85)', stroke: '#14532d' }
                };
                const color = paleta[estado] || paleta.pendiente;
                return new Style({
                    image: new Circle({
                        radius: 6,
                        fill: new Fill({ color: color.fill }),
                        stroke: new Stroke({ color: color.stroke, width: 1.5 })
                    })
                });
            },
            visible: false,
            zIndex: 8
        });
        osmStagingLayerRef.current = osmStagingLayer;

        const highlightLayer = new VectorLayer({
            source: highlightSourceRef.current,
            style: (feature) => {
                const geomType = feature.getGeometry().getType();
                if (geomType === 'Point') {
                    return new Style({
                        image: new Circle({
                            radius: 14,
                            fill: new Fill({ color: 'rgba(255, 200, 0, 0.9)' }),
                            stroke: new Stroke({ color: '#ff6600', width: 3 }),
                        }),
                    });
                }
                return new Style({
                    fill: new Fill({ color: 'rgba(255, 200, 0, 0.4)' }),
                    stroke: new Stroke({ color: '#ff6600', width: 3 }),
                });
            },
            zIndex: 10
        });
        highlightLayerRef.current = highlightLayer;

        // Crear capa ARBA
        const arbaWmsLayer = new TileLayer({
            source: new TileWMS({
                url: 'https://geo.arba.gov.ar/geoserver/idera/wms',
                params: {
                    'LAYERS': 'idera:Parcela',
                    'TILED': true,
                    'FORMAT': 'image/png',
                    'TRANSPARENT': true,
                    'VERSION': '1.1.1',
                    'SRS': 'EPSG:3857'
                },
                attributions: '© ARBA',
                crossOrigin: 'anonymous'
            }),
            visible: false,
            zIndex: 15,
            opacity: 0.8
        });
        arbaLayerRef.current = arbaWmsLayer;

        const initialMap = new Map({
            target: mapRef.current,
            layers: [
                osmLayer.current,
                satelliteLayer.current,
                topoLayer.current,
                ignLayer.current,
                positronLayer.current,
                voyagerLayer.current,
                darkLayer.current,
                arbaWmsLayer,
                municipiosLayer,
                vectorLayer,
                parcelasLayer,
                osmStagingLayer,
                sedeLayer,
                highlightLayer
            ],
            view: new View({
                center: [-60.50, -37.16], // Provincia de Buenos Aires
                zoom: 7,
                minZoom: 6,
                maxZoom: 18,
                extent: [-65, -42, -55, -33],
                constrainOnlyCenter: true,
                smoothExtentConstraint: true
            }),
            controls: defaultControls({ zoom: false }).extend([
                new Zoom({
                    className: 'ol-zoom ol-zoom-right'
                })
            ])
        });

        // Click handler para selección y resaltado usando capa dedicada
        initialMap.on('click', (e) => {
            const pixel = initialMap.getPixelFromCoordinate(e.coordinate);
            let clickedFeature = null;
            let clickedOsm = null;
            
            initialMap.forEachFeatureAtPixel(pixel, (feature, layer) => {
                if (layer === osmStagingLayerRef.current) {
                    clickedOsm = feature;
                    return true;
                }
                if (layer === sedeLayerRef.current || layer === parcelasLayerRef.current) {
                    clickedFeature = feature;
                    return true;
                }
            });
            
            highlightSourceRef.current.clear();

            if (clickedOsm) {
                const sel = buildOsmSelection(clickedOsm.getProperties());
                setOsmSeleccionada(sel);
                setOsmNombreEditado(sel.nombre_completo || '');
                setOsmDomicilioEditado(sel.domicilio || '');
                setOsmAccionError(null);
                setOsmAccionMsg(null);
                setOsmRejectMode(false);
                setOsmObservaciones('');
                setEntidadSeleccionada(null);
                return;
            }
            setOsmSeleccionada(null);
            
            if (clickedFeature) {
                let targetFeature = clickedFeature;
                let idRed = null;
                
                const clusteredFeatures = clickedFeature.get('features');
                
                if (clusteredFeatures && clusteredFeatures.length > 0) {
                    if (clusteredFeatures.length === 1) {
                        targetFeature = clusteredFeatures[0];
                        idRed = targetFeature.get('id_red');
                    } else {
                        const groupExtent = getClusterExtent(clusteredFeatures);
                        if (groupExtent) {
                            initialMap.getView().fit(groupExtent, {
                                padding: [80, 80, 80, 80],
                                duration: 500,
                                maxZoom: 15
                            });
                        }
                        return;
                    }
                } else {
                    idRed = clickedFeature.get('id_red');
                }
                
                if (targetFeature && idRed) {
                    const properties = targetFeature.getProperties();
                    
                    setEntidadSeleccionada({
                        id: properties.id,
                        nombre_completo: properties.nombre_completo,
                        nombre_fantasia: properties.nombre_fantasia,
                        tipo_entidad: properties.tipo_entidad,
                        municipio: properties.municipio,
                        codigo_partido: properties.codigo_partido,
                        id_red: idRed
                    });
                    
                    if (isPrivate) {
                        // Modo Privado: Carga de detalles detallados / restricción
                        if (isEditorEntidad && idRed !== entidadId) {
                            setEntidadDetalle({ 
                                acceso_restringido: true,
                                mensaje: "Solo puede ver datos completos de su entidad."
                            });
                            setLoadingDetalle(false);
                        } else if (isVisualizador && properties.codigo_partido !== userMunicipio) {
                            setEntidadDetalle({ 
                                acceso_restringido: true,
                                mensaje: "Solo puede ver datos completos de entidades de su municipio."
                            });
                            setLoadingDetalle(false);
                        } else {
                            setLoadingDetalle(true);
                            fetch(`/api/entidades/datos_entidad/${idRed}`)
                                .then(res => res.json())
                                .then(data => {
                                    setEntidadDetalle(data);
                                })
                                .catch(err => {
                                    console.error('Error cargando detalle:', err);
                                })
                                .finally(() => {
                                    setLoadingDetalle(false);
                                });
                        }
                    } else {
                        // Modo Público: no hace fetch de detalles privados
                        setEntidadDetalle(null);
                    }
                    
                    // Resaltar features
                    if (sedeSource.current) {
                        sedeSource.current.getFeatures().forEach(f => {
                            if (f.get('id_red') === idRed) {
                                highlightSourceRef.current.addFeature(f);
                            }
                        });
                    }
                    if (parcelasLayerRef.current) {
                        parcelasLayerRef.current.getSource().forEachFeature(f => {
                            if (f.get('id_red') === idRed) {
                                highlightSourceRef.current.addFeature(f);
                            }
                        });
                    }
                }
            } else {
                setEntidadSeleccionada(null);
            }
        });

        // Hover simple - tooltip
        initialMap.on('pointermove', (e) => {
            const pixel = initialMap.getPixelFromCoordinate(e.coordinate);
            setMousePosition({ x: pixel[0], y: pixel[1] });
            
            let foundFeature = null;
            let foundLayer = null;
            initialMap.forEachFeatureAtPixel(pixel, (feature, layer) => {
                if (layer === osmStagingLayerRef.current || layer === sedeLayerRef.current || layer === parcelasLayerRef.current) {
                    foundFeature = feature;
                    foundLayer = layer;
                    return true;
                }
            });
            
            if (foundFeature) {
                let targetFeature = foundFeature;
                let displayProperties = {};
                
                if (foundLayer === sedeLayerRef.current && clusterSource.current) {
                    const clusteredFeatures = foundFeature.get('features');
                    if (clusteredFeatures && clusteredFeatures.length > 0) {
                        if (clusteredFeatures.length === 1) {
                            targetFeature = clusteredFeatures[0];
                            const props = targetFeature.getProperties();
                            displayProperties = {
                                id: props.id,
                                nombre_completo: props.nombre_completo,
                                tipo_entidad: props.tipo_entidad,
                                id_red: props.id_red,
                                municipio: props.municipio
                            };
                        } else {
                            const firstFeature = clusteredFeatures[0];
                            const props = firstFeature.getProperties();
                            displayProperties = {
                                id: null,
                                nombre_completo: `${clusteredFeatures.length} entidades en este área`,
                                tipo_entidad: props.tipo_entidad,
                                id_red: null,
                                municipio: props.municipio
                            };
                        }
                    }
                } else {
                    const props = foundFeature.getProperties();
                    displayProperties = {
                        id: props.id,
                        nombre_completo: props.nombre_completo,
                        tipo_entidad: props.tipo_entidad,
                        id_red: props.id_red,
                        municipio: props.municipio
                    };
                }
                
                setHoveredEntity(displayProperties);
            } else {
                setHoveredEntity(null);
            }
        });
        
        setMap(initialMap);

        return () => {
            initialMap.setTarget(undefined);
        };
    }, []);

    // Controlar visibilidad de capas
    useEffect(() => {
        if (map) {
            if (sedeLayerRef.current) {
                sedeLayerRef.current.setVisible(showSedes);
            }
            if (parcelasLayerRef.current) {
                parcelasLayerRef.current.setVisible(showParcelas);
            }
            if (municipiosLayerRef.current) {
                municipiosLayerRef.current.setVisible(showMunicipios);
            }
        }
    }, [map, showSedes, showParcelas, showMunicipios]);

    // Cargar límites de todos los municipios (una sola vez)
    useEffect(() => {
        if (!map || !showMunicipios) return;
        if (municipiosSource.current.getFeatures().length > 0) return;

        let cancelled = false;

        const cargarMunicipios = async () => {
            try {
                const response = await fetch('/api/partidos_geom');
                if (!response.ok) throw new Error('Error fetching municipios');
                const geojson = await response.json();
                if (cancelled || !geojson?.features?.length) return;
                const features = new GeoJSON().readFeatures(geojson, {
                    featureProjection: 'EPSG:4326',
                    dataProjection: 'EPSG:4326'
                });
                municipiosSource.current.addFeatures(features);
            } catch (error) {
                console.error('Error cargando límites de municipios:', error);
            }
        };

        cargarMunicipios();

        return () => {
            cancelled = true;
        };
    }, [map, showMunicipios]);

    // Pintar los partidos según el filtro de referente municipal
    useEffect(() => {
        if (!map || !municipiosLayerRef.current) return;
        const filtro = selectedReferente;

        municipiosLayerRef.current.setStyle((feature) => {
            const hasReferente = feature.get('has_referente_municipal') === true;
            let fill = 'rgba(100, 116, 139, 0.04)';
            let stroke = 'rgba(100, 116, 139, 0.7)';
            let width = 1;

            if (filtro === 'con' && hasReferente) {
                fill = 'rgba(34, 197, 94, 0.18)';
                stroke = '#16a34a';
                width = 1.75;
            } else if (filtro === 'sin' && !hasReferente) {
                fill = 'rgba(239, 68, 68, 0.16)';
                stroke = '#dc2626';
                width = 1.75;
            }

            return new Style({
                fill: new Fill({ color: fill }),
                stroke: new Stroke({ color: stroke, width })
            });
        });

        municipiosSource.current.changed();
    }, [map, selectedReferente]);

    // Capa ARBA
    useEffect(() => {
        if (arbaLayerRef.current) {
            arbaLayerRef.current.setVisible(showArba);
        }
    }, [showArba]);

    // Capas base
    useEffect(() => {
        if (map) {
            osmLayer.current.setVisible(baseLayer === 'osm');
            satelliteLayer.current.setVisible(baseLayer === 'satellite');
            topoLayer.current.setVisible(baseLayer === 'topo');
            ignLayer.current.setVisible(baseLayer === 'ign');
            positronLayer.current.setVisible(baseLayer === 'positron');
            voyagerLayer.current.setVisible(baseLayer === 'voyager');
            darkLayer.current.setVisible(baseLayer === 'dark');
        }
    }, [map, baseLayer]);

    // Refrescar estilo de la capa
    useEffect(() => {
        if (!sedeLayerRef.current) return;

        if (styleMode === "byType" || (isPrivate && styleMode === "byEditor")) {
            sedeLayerRef.current.setSource(sedeSource.current);
        } else {
            sedeLayerRef.current.setSource(clusterSource.current);
        }

        const dynamicClusterStyle = (feature) => {
            const features = feature.get('features');
            const size = features ? features.length : 1;

            if (size > 1) {
                return new Style({
                    image: new Circle({
                        radius: 15 + Math.min(size * 2, 15),
                        fill: new Fill({
                            color: 'rgba(59, 130, 246, 0.8)'
                        }),
                        stroke: new Stroke({
                            color: '#fff',
                            width: 2
                        })
                    }),
                    text: new Text({
                        text: size.toString(),
                        fill: new Fill({
                            color: '#fff'
                        }),
                        font: 'bold 12px sans-serif',
                        offsetY: 1
                    })
                });
            } else {
                const singleFeature = features ? features[0] : feature;
                const tipo = singleFeature.get("tipo_entidad") || "";
                const hasEditor = singleFeature.get("has_editor");
                const codigoPartido = singleFeature.get("codigo_partido");

                if (selectedPartido && String(codigoPartido).padStart(3, '0') !== selectedPartido) {
                    return null;
                }

                let color;
                if (styleMode === "byType") {
                    color = getColorByTipo(tipo);
                } else if (isPrivate && styleMode === "byEditor") {
                    color = (hasEditor === true) ? "#10b981" : "#3b82f6";
                } else {
                    color = "#3b82f6";
                }

                return new Style({
                    image: new Circle({
                        radius: 8,
                        fill: new Fill({ color: color }),
                        stroke: new Stroke({ color: '#fff', width: 2 })
                    })
                });
            }
        };

        sedeLayerRef.current.setStyle(dynamicClusterStyle);
        
        if (sedeSource.current) {
            const allFeatures = sedeSource.current.getFeatures();
            if (selectedPartido) {
                const visibleCount = allFeatures.filter(f => {
                    const codigoPartido = f.get("codigo_partido");
                    return String(codigoPartido).padStart(3, '0') === selectedPartido;
                }).length;
                setFilteredCount(visibleCount);
            } else {
                setFilteredCount(allFeatures.length);
            }
        }
    }, [styleMode, selectedPartido, isPrivate]);

    // Efecto para filtrar por partido (zoom y geom)
    useEffect(() => {
        if (!map) return;

        const fetchPartidoAndZoom = async () => {
            if (!selectedPartido) {
                if (partidoLayerRef.current) {
                    map.removeLayer(partidoLayerRef.current);
                    partidoLayerRef.current = null;
                }
                
                map.getView().animate({
                    center: [-60.50, -37.16],
                    zoom: 7,
                    duration: 1000
                });
                return;
            }

            try {
                const response = await fetch(`/api/partido_geom/${selectedPartido}`);
                if (!response.ok) throw new Error('Error fetching partido');
                
                const geojsonData = await response.json();
                
                if (geojsonData?.features?.length > 0) {
                    const sourcePartido = new VectorSource({
                        features: new GeoJSON().readFeatures(geojsonData, {
                            featureProjection: 'EPSG:4326',
                            dataProjection: 'EPSG:4326'
                        })
                    });

                    if (partidoLayerRef.current) {
                        map.removeLayer(partidoLayerRef.current);
                    }

                    const layerPartido = new VectorLayer({
                        source: sourcePartido,
                        style: new Style({
                            fill: new Fill({ color: 'rgba(255, 0, 0, 0.1)' }),
                            stroke: new Stroke({ color: 'red', width: 2, lineDash: [4, 8] }),
                        }),
                    });

                    map.addLayer(layerPartido);
                    partidoLayerRef.current = layerPartido;

                    if (!selectedEntidadId) {
                        map.getView().fit(sourcePartido.getExtent(), {
                            padding: [50, 50, 50, 50],
                            duration: 1000,
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching partido geometry:', error);
            }
        };

        fetchPartidoAndZoom();

        return () => {
            if (partidoLayerRef.current && map) {
                map.removeLayer(partidoLayerRef.current);
                partidoLayerRef.current = null;
            }
        };
    }, [map, selectedPartido, selectedEntidadId]);

    // Zoom a entidad seleccionada externamente
    useEffect(() => {
        if (!map || !zoomTo || !zoomTo.id_red) return;

        setSelectedEntidadId(zoomTo.id_red);
        
        if (zoomTo.codigo_partido) {
            setSelectedPartido(zoomTo.codigo_partido.toString().padStart(3, '0'));
        }

        if (zoomTo.geom_sede) {
            setShowSedes(true);
        }
        if (zoomTo.geom_parcela && Array.isArray(zoomTo.geom_parcela) && zoomTo.geom_parcela.length > 0) {
            setShowParcelas(true);
        }

        const zoomFeatures = [];
        
        if (zoomTo.geom_sede) {
            const sedeGeoJSON = typeof zoomTo.geom_sede === 'string' 
                ? JSON.parse(zoomTo.geom_sede) 
                : zoomTo.geom_sede;
            
            const sedeFeature = new GeoJSON().readFeature(sedeGeoJSON, {
                featureProjection: 'EPSG:4326',
                dataProjection: 'EPSG:4326'
            });
            sedeFeature.set('id_red', zoomTo.id_red);
            sedeFeature.set('nombre', zoomTo.nombre_completo);
            zoomFeatures.push(sedeFeature);
            
            // Resaltar en la capa de highlight (hace que se dibuje de forma individual e independiente sobre el cluster)
            if (highlightSourceRef.current) {
                highlightSourceRef.current.clear();
                highlightSourceRef.current.addFeature(sedeFeature);
            }
        }
        
        if (zoomTo.geom_parcela && Array.isArray(zoomTo.geom_parcela)) {
            zoomTo.geom_parcela.forEach((geom) => {
                try {
                    if (geom && typeof geom === 'object' && geom.geometry && geom.geometry.type) {
                        const featureGeoJSON = {
                            type: 'Feature',
                            geometry: geom.geometry,
                            properties: {
                                partida: geom.partida,
                                parcela: geom.parcela
                            }
                        };
                        const parcelaFeature = new GeoJSON().readFeature(featureGeoJSON, {
                            featureProjection: 'EPSG:4326',
                            dataProjection: 'EPSG:4326'
                        });
                        parcelaFeature.set('id_red', zoomTo.id_red);
                        parcelaFeature.set('nombre', zoomTo.nombre_completo);
                        zoomFeatures.push(parcelaFeature);
                        
                        if (highlightSourceRef.current) {
                            highlightSourceRef.current.addFeature(parcelaFeature);
                        }
                    }
                } catch (err) {
                    console.warn('Error parsing parcela geometry:', err, geom);
                }
            });
        }

        // Seleccionar automáticamente la entidad para abrir el panel lateral con la información de la misma
        setEntidadSeleccionada({
            id_red: zoomTo.id_red,
            nombre_completo: zoomTo.nombre_completo,
            nombre_fantasia: zoomTo.nombre_fantasia || "",
            tipo_entidad: zoomTo.tipo_entidad || "club",
            municipio: zoomTo.partido_nombre || "",
            codigo_partido: zoomTo.codigo_partido
        });
        
        setShowInfo(true);

        if (isPrivate) {
            if (isEditorEntidad && zoomTo.id_red !== entidadId) {
                setEntidadDetalle({ 
                    acceso_restringido: true,
                    mensaje: "Solo puede ver datos completos de su entidad."
                });
            } else if (isVisualizador && zoomTo.codigo_partido !== userMunicipio) {
                setEntidadDetalle({ 
                    acceso_restringido: true,
                    mensaje: "Solo puede ver datos completos de entidades de su municipio."
                });
            } else {
                setLoadingDetalle(true);
                fetch(`/api/entidades/datos_entidad/${zoomTo.id_red}`)
                    .then(res => res.json())
                    .then(data => {
                        setEntidadDetalle(data);
                    })
                    .catch(err => {
                        console.error('Error cargando detalle de entidad zoomTo:', err);
                    })
                    .finally(() => {
                        setLoadingDetalle(false);
                    });
            }
        } else {
            setEntidadDetalle(null);
        }

        if (zoomFeatures.length > 0) {
            const zoomSource = new VectorSource({
                features: zoomFeatures
            });
            
            const extent = zoomSource.getExtent();
            
            setTimeout(() => {
                if (extent && extent[0] !== Infinity) {
                    map.getView().fit(extent, {
                        padding: [100, 100, 100, 100],
                        duration: 1000,
                        maxZoom: 18
                    });
                }
            }, 500);
        }
    }, [map, zoomTo, isPrivate, isEditorEntidad, entidadId]);

    // Cargar sedes desde API
    useEffect(() => {
        if (!map || selectedPartido === null) return;
        
        const cargarSedes = async () => {
            try {
                const partidoFiltro = selectedPartido || (filterByMunicipio ? filterByMunicipio.toString().padStart(3, '0') : "");
                
                const sedesParams = new URLSearchParams();
                if (selectedTipoEntidad) sedesParams.set('tipo_entidad', selectedTipoEntidad);
                if (selectedReferente) sedesParams.set('referente_municipal', selectedReferente);
                const sedesQuery = sedesParams.toString();

                let sedesUrl = partidoFiltro
                    ? `/api/entidades/geojson/sedes/${partidoFiltro}`
                    : '/api/entidades/geojson/sedes';
                if (sedesQuery) sedesUrl += `?${sedesQuery}`;

                sedeSource.current.clear();
                
                // Cargar sedes (puntos)
                const sedesResponse = await fetch(sedesUrl);
                const sedesGeoJSON = await sedesResponse.json();

                if (sedesGeoJSON.features && sedesGeoJSON.features.length > 0) {
                    const sedesFeatures = new GeoJSON().readFeatures(sedesGeoJSON, {
                        featureProjection: "EPSG:4326",
                        dataProjection: "EPSG:4326"
                    });
                    
                    let filteredSedes = sedesFeatures;
                    if (selectedEntidadId) {
                        filteredSedes = sedesFeatures.filter(f => {
                            const idRed = f.get('id_red') || '';
                            return idRed === selectedEntidadId;
                        });
                    }
                    
                    sedeSource.current.addFeatures(filteredSedes);
                    setFilteredCount(filteredSedes.length);
                } else {
                    setFilteredCount(0);
                }

                setEntidades(sedesGeoJSON.features || []);
                
                // Resaltado de entidad del usuario al cargar (para editor_entidad)
                if (isEditorEntidad && entidadId && sedeSource.current && highlightSourceRef.current) {
                    setTimeout(() => {
                        const allFeatures = sedeSource.current.getFeatures();
                        const userFeature = allFeatures.find(f => f.get('id_red') === entidadId);
                        if (userFeature) {
                            highlightSourceRef.current.clear();
                            highlightSourceRef.current.addFeature(userFeature);
                        }
                    }, 300);
                }
            } catch (error) {
                console.error("Error cargando sedes:", error);
            }
        };
        
        cargarSedes();
    }, [map, selectedTipoEntidad, selectedPartido, selectedReferente, isPrivate, refreshSedesKey]);

    // Cargar parcelas desde API (Lazy-loading: Solo cuando showParcelas sea true)
    useEffect(() => {
        if (!map || selectedPartido === null) return;

        if (!showParcelas) {
            parcelasSource.current.clear();
            return;
        }
        
        const cargarParcelas = async () => {
            try {
                const partidoFiltro = selectedPartido || (filterByMunicipio ? filterByMunicipio.toString().padStart(3, '0') : "");
                
                const parcelasParams = new URLSearchParams();
                if (selectedTipoEntidad) parcelasParams.set('tipo_entidad', selectedTipoEntidad);
                if (selectedReferente) parcelasParams.set('referente_municipal', selectedReferente);
                const parcelasQuery = parcelasParams.toString();

                let parcelasUrl = partidoFiltro
                    ? `/api/entidades/geojson/parcelas/${partidoFiltro}`
                    : '/api/entidades/geojson/parcelas';
                if (parcelasQuery) parcelasUrl += `?${parcelasQuery}`;

                parcelasSource.current.clear();

                const parcelasResponse = await fetch(parcelasUrl);
                const parcelasGeoJSON = await parcelasResponse.json();

                if (parcelasGeoJSON.features && parcelasGeoJSON.features.length > 0) {
                    const parcelasFeatures = new GeoJSON().readFeatures(parcelasGeoJSON, {
                        featureProjection: "EPSG:4326",
                        dataProjection: "EPSG:4326"
                    });
                    
                    let filteredParcelas = parcelasFeatures;
                    if (selectedEntidadId) {
                        filteredParcelas = parcelasFeatures.filter(f => {
                            const idRed = f.get('id_red') || '';
                            return idRed === selectedEntidadId;
                        });
                    }
                    
                    parcelasSource.current.addFeatures(filteredParcelas);
                }
            } catch (error) {
                console.error("Error cargando parcelas:", error);
            }
        };
        
        cargarParcelas();
    }, [map, selectedTipoEntidad, selectedPartido, selectedReferente, showParcelas, isPrivate]);

    // Zoom al punto OSM actualmente seleccionado
    const osmSeleccionadaCoords = (() => {
        if (!osmSeleccionada) return null;
        if (Number.isFinite(osmSeleccionada.lon) && Number.isFinite(osmSeleccionada.lat)) {
            return [osmSeleccionada.lon, osmSeleccionada.lat];
        }
        if (!osmStagingSource.current) return null;
        const feature = osmStagingSource.current.getFeatures().find(
            (f) => String(f.get('id')) === String(osmSeleccionada.id)
        );
        const geom = feature && feature.getGeometry();
        return geom ? geom.getCoordinates() : null;
    })();

    const zoomToOsmSeleccionada = () => {
        if (!map || !osmSeleccionadaCoords) return;
        map.getView().animate({ center: osmSeleccionadaCoords, zoom: 17, duration: 600 });
    };

    // Enfocar un registro OSM puntual enviado desde la página de Validación OSM
    useEffect(() => {
        if (!zoomTo?.evento_id || !canViewOsmStaging) return;
        setShowOsmStaging(true);
        if (zoomTo.codigo_partido) {
            setSelectedPartido(zoomTo.codigo_partido.toString().padStart(3, '0'));
        }
    }, [zoomTo, canViewOsmStaging]);

    // Cargar entidades OSM sin validar (capa staging, solo admin / provincial especial)
    useEffect(() => {
        if (!map || !canViewOsmStaging) return;

        const layer = osmStagingLayerRef.current;

        if (!showOsmStaging) {
            osmStagingSource.current.clear();
            if (layer) layer.setVisible(false);
            setOsmSeleccionada(null);
            setOsmRejectMode(false);
            setOsmObservaciones('');
            return;
        }

        const cargarOsmStaging = async () => {
            try {
                const partidoFiltro = osmPartidoFiltro || "";
                const url = partidoFiltro
                    ? `/api/entidades/geojson/osm-staging/${partidoFiltro}`
                    : '/api/entidades/geojson/osm-staging';

                const response = await fetch(url);
                const geojson = await response.json();

                osmStagingSource.current.clear();

                if (geojson.features && geojson.features.length > 0) {
                    const features = new GeoJSON().readFeatures(geojson, {
                        featureProjection: "EPSG:4326",
                        dataProjection: "EPSG:4326"
                    });
                    osmStagingSource.current.addFeatures(features);
                }

                if (zoomTo?.evento_id) {
                    const focus = osmStagingSource.current.getFeatures().find(
                        (f) => String(f.get('id')) === String(zoomTo.evento_id)
                    );
                    if (focus) {
                        const sel = buildOsmSelection(focus.getProperties());
                        setOsmSeleccionada(sel);
                        setOsmNombreEditado(sel.nombre_completo || '');
                        setOsmDomicilioEditado(sel.domicilio || '');
                        setOsmAccionError(null);
                        setOsmAccionMsg(null);
                        setOsmRejectMode(false);
                        setOsmObservaciones('');
                        setEntidadSeleccionada(null);
                        const geom = focus.getGeometry();
                        if (geom) {
                            map.getView().animate({ center: geom.getCoordinates(), zoom: 16, duration: 600 });
                        }
                    }
                }

                if (layer) layer.setVisible(true);
            } catch (error) {
                console.error("Error cargando entidades OSM sin validar:", error);
            }
        };

        cargarOsmStaging();
    }, [map, canViewOsmStaging, showOsmStaging, osmPartidoFiltro, zoomTo]);

    // Ejecutar importación o rechazo de una entidad OSM seleccionada
    const ejecutarAccionOsm = async (accion, observaciones = '') => {
        if (!osmSeleccionada || !token) {
            setOsmAccionError('No hay sesión válida para realizar la acción');
            return;
        }

        if (accion === 'reject' && !observaciones.trim()) {
            setOsmAccionError('Indicá el motivo del rechazo.');
            return;
        }

        setOsmAccionLoading(true);
        setOsmAccionError(null);
        setOsmAccionMsg(null);

        try {
            const res = await fetch(`/api/entidades/osm-staging/${osmSeleccionada.id}/${accion}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: accion === 'reject'
                    ? JSON.stringify({ observaciones: observaciones.trim() })
                    : JSON.stringify({
                        nombre_completo: osmNombreEditado.trim(),
                        domicilio: osmDomicilioEditado.trim()
                    })
            });
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.error || data.message || 'No se pudo completar la acción');
            }

            // Actualizar el color de la marca según el nuevo estado (no se quita del mapa)
            const nuevoEstado = accion === 'import' ? 'importado' : 'rechazado';
            osmStagingSource.current.getFeatures().forEach(f => {
                if (f.get('id') === osmSeleccionada.id) {
                    f.set('estado', nuevoEstado);
                    if (accion === 'reject') {
                        f.set('observaciones', observaciones.trim());
                    } else {
                        if (osmNombreEditado.trim()) f.set('nombre_completo', osmNombreEditado.trim());
                        if (osmDomicilioEditado.trim()) f.set('domicilio', osmDomicilioEditado.trim());
                    }
                }
            });

            setOsmSeleccionada(prev => prev ? {
                ...prev,
                estado: accion === 'import' ? 'importado' : 'rechazado',
                entidad_id_red: data.entidad_id_red || prev.entidad_id_red,
                observaciones: accion === 'reject' ? observaciones.trim() : prev.observaciones,
                nombre_completo: accion === 'import' && osmNombreEditado.trim() ? osmNombreEditado.trim() : prev.nombre_completo,
                domicilio: accion === 'import' && osmDomicilioEditado.trim() ? osmDomicilioEditado.trim() : prev.domicilio
            } : prev);
            setOsmRejectMode(false);
            setOsmObservaciones('');
            setOsmAccionMsg(
                accion === 'import'
                    ? `Entidad importada correctamente (ID ${data.entidad_id_red})`
                    : 'Registro rechazado'
            );

            if (accion === 'import') {
                setRefreshSedesKey(k => k + 1);
            }
        } catch (error) {
            setOsmAccionError(error.message || 'Error al procesar la acción');
        } finally {
            setOsmAccionLoading(false);
        }
    };

    // Cargar datos privados de la entidad del usuario (solo editor_entidad)
    useEffect(() => {
        const cargarDatosEntidadUsuario = async () => {
            if (isEditorEntidad && entidadId && map) {
                try {
                    setEntidadDetalle(null);
                    setLoadingDetalle(true);
                    
                    const response = await fetch(`/api/entidades/datos_entidad/${entidadId}`);
                    if (response.ok) {
                        const data = await response.json();
                        const geomSedeData = data.identidad?.geom_sede;
                        
                        if (geomSedeData && map) {
                            try {
                                const geomSede = typeof geomSedeData === 'string' 
                                    ? JSON.parse(geomSedeData) 
                                    : geomSedeData;
                                
                                if (geomSede && geomSede.coordinates) {
                                    setLoadingZoomEntidad(true);
                                    
                                    const tempFeature = new GeoJSON().readFeature(geomSede, {
                                        featureProjection: 'EPSG:4326',
                                        dataProjection: 'EPSG:4326'
                                    });
                                    const extent = tempFeature.getGeometry().getExtent();
                                    
                                    setTimeout(() => {
                                        map.getView().fit(extent, {
                                            padding: [100, 100, 100, 100],
                                            duration: 1000,
                                            maxZoom: 18
                                        });
                                        setLoadingZoomEntidad(false);
                                    }, 1000);
                                }
                            } catch (e) {
                                console.warn('No se pudo hacer zoom a la entidad:', e);
                                setLoadingZoomEntidad(false);
                            }
                        }
                        
                        setEntidadDetalle(data);
                        setShowInfo(true);
                    }
                } catch (error) {
                    console.error('Error cargando datos de entidad del usuario:', error);
                } finally {
                    setLoadingDetalle(false);
                }
            }
        };
        
        cargarDatosEntidadUsuario();
    }, [isEditorEntidad, entidadId, map]);

    // Resaltar la entidad del usuario cuando se carguen las features
    useEffect(() => {
        if (isEditorEntidad && entidadId && sedeSource.current && highlightSourceRef.current && entidades.length > 0) {
            const userFeature = sedeSource.current.getFeatures().find(f => f.get('id_red') === entidadId);
            if (userFeature) {
                highlightSourceRef.current.clear();
                highlightSourceRef.current.addFeature(userFeature);
            }
        }
    }, [isEditorEntidad, entidadId, entidades]);

    return (
        <div className={`w-full h-full flex flex-col relative ${className}`}>
            <h2 className="text-2xl font-bold text-white absolute top-2 left-4 z-20 drop-shadow-lg" style={{ backgroundColor: 'transparent', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                Registro Provincial de Entidades Deportivas - RED
            </h2>
            <div ref={mapRef} className="flex-1 w-full min-h-0 relative" />
            
            {/* Mensaje de carga durante zoom a entidad */}
            {loadingZoomEntidad && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 z-50">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm font-medium">Obteniendo ubicación de su entidad...</span>
                </div>
            )}

            {/* Buscador - Header siempre visible, contenido colapsable */}
            <div className="absolute top-4 right-4 z-20 w-72">
                <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 flex items-center justify-between font-semibold text-sm text-white bg-gradient-to-r from-slate-700 to-slate-600">
                        <button
                            onClick={() => setShowSearch(!showSearch)}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <span className="font-semibold text-sm">Buscar Entidad</span>
                        </button>
                        <div className="flex items-center gap-2">
                            {selectedEntidadId && (
                                <button
                                    onClick={clearSearchAndResetZoom}
                                    className="flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors z-10"
                                    title="Restablecer vista"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span>Restablecer</span>
                                </button>
                            )}
                            <button
                                onClick={() => setShowSearch(!showSearch)}
                                className="hover:opacity-80 transition-opacity"
                            >
                                <span className={`transform transition-transform ${showSearch ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                        </div>
                    </div>
                    {showSearch && (
                        <div className="p-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        handleSearch(e.target.value);
                                    }}
                                    placeholder="Nombre, fantasía o ID..."
                                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                                        title="Limpiar búsqueda"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            {searchResults.length > 0 && (
                                <div className="mt-2 max-h-48 overflow-y-auto">
                                    {searchResults.some(r => r.mapeable) && (
                                        <>
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1">
                                                Con sede
                                            </div>
                                            {searchResults.filter(r => r.mapeable).map((result, index) => (
                                                <button
                                                    key={`map-${index}`}
                                                    onClick={() => zoomToSearchResult(result)}
                                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                                                >
                                                    <div className="font-medium text-gray-800">{result.nombre}</div>
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                                        <span>{result.idRed}</span>
                                                        <span>• {result.type === 'sede' ? 'Sede' : 'Parcela'}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                    {searchResults.some(r => !r.mapeable) && (
                                        <>
                                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-1 mt-1 border-t border-gray-200 pt-2">
                                                Sin georreferenciar
                                            </div>
                                            {searchResults.filter(r => !r.mapeable).map((result, index) => (
                                                <button
                                                    key={`nomap-${index}`}
                                                    onClick={() => zoomToSearchResult(result)}
                                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                                                >
                                                    <div className="font-medium text-gray-700 flex items-center gap-1.5">
                                                        {result.nombre}
                                                        <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">Sin sede</span>
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        {result.idRed} • No mapeable
                                                    </div>
                                                </button>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                            {searchQuery && searchResults.length === 0 && (
                                <div className="mt-2 text-center text-sm text-gray-500 py-2">
                                    No se encontraron resultados
                                </div>
                            )}
                            {/* Toast de información */}
                            {infoMessage && (
                                <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg">
                                    {infoMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* CSS para mover zoom y atribución */}
            <style>{`
                .ol-zoom {
                    left: auto !important;
                    right: 19rem !important;
                    top: 1rem !important;
                }
                .ol-attribution {
                    bottom: 1em !important;
                    left: 0.5em !important;
                    right: auto !important;
                    max-width: calc(100% - 1em) !important;
                    z-index: 1000 !important;
                }
                .ol-attribution ul {
                    font-size: 10px !important;
                    line-height: 1.2 !important;
                }
            `}</style>
            
            {/* Menú Acordeón - Lado Izquierdo */}
            <div className={`absolute ${isPrivate ? 'top-14' : 'top-16'} left-4 flex flex-col gap-2 z-10 w-64`}>
                {/* Sección Herramientas */}
                <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                    <button
                        onClick={() => setShowTools(!showTools)}
                        className="w-full px-4 py-3 flex items-center justify-between font-semibold text-sm text-white bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 transition-all"
                    >
                        <span className="flex items-center gap-2">
                            <ToolsIcon />
                            Herramientas
                        </span>
                        <span className={`transform transition-transform ${showTools ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {showTools && (
                        <div className="p-3 space-y-3">
                            {/* SECCIÓN DE FILTROS */}
                            <div className="border-b border-gray-200 pb-3 mb-3">
                                <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Filtros</h4>
                                
                                {/* Selector de Partido */}
                                <div className="mb-3">
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Partido</label>
                                    <select
                                        value={selectedPartido || ""}
                                        onChange={(e) => {
                                            setSelectedPartido(e.target.value);
                                            setSelectedEntidadId(null);
                                            setEntidadSeleccionada(null);
                                            highlightSourceRef.current?.clear();
                                        }}
                                        disabled={isPrivate && (isEditorEntidad || !!filterByMunicipio)}
                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-500"
                                    >
                                        <option value="">Todos los partidos</option>
                                        {optionsPartido.map(partido => (
                                            <option key={partido.value} value={partido.value}>
                                                {partido.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                {/* Selector Tipo Entidad */}
                                <div className="mb-3">
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de Entidad</label>
                                    <select
                                        value={selectedTipoEntidad}
                                        onChange={(e) => setSelectedTipoEntidad(e.target.value)}
                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                    >
                                        <option value="">Todas</option>
                                        {optionsTipoEntidad.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>
                                                {tipo.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Selector Referente Municipal */}
                                <div className="mb-3">
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Referente Municipal</label>
                                    <select
                                        value={selectedReferente}
                                        onChange={(e) => setSelectedReferente(e.target.value)}
                                        className="w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                    >
                                        <option value="">Todos</option>
                                        <option value="con">Con referente municipal</option>
                                        <option value="sin">Sin referente municipal</option>
                                    </select>
                                    {selectedReferente === 'con' && (
                                        <p className="text-[11px] text-green-700 mt-1">Verde: partidos con referente municipal.</p>
                                    )}
                                    {selectedReferente === 'sin' && (
                                        <p className="text-[11px] text-red-700 mt-1">Rojo: partidos sin referente municipal.</p>
                                    )}
                                </div>
                            </div>
                            
                            {/* Selector de Estilo */}
                            <div className="pt-3 border-t border-gray-200">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Herramienta de Estilos</label>
                                <select
                                    value={styleMode}
                                    onChange={(e) => setStyleMode(e.target.value)}
                                    className="w-full text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                >
                                    <option value="single">Un solo color (azul)</option>
                                    <option value="byType">Por tipo de entidad</option>
                                    {isPrivate && (
                                        <option value="byEditor">Con/Sin editor asignado</option>
                                    )}
                                </select>
                            </div>

                            {/* Botón Info */}
                            <button
                                onClick={() => setShowInfo(!showInfo)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                                    showInfo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <InfoIcon />
                                {showInfo ? 'Ocultar info' : 'Mostrar info'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Sección Capas */}
                <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                    <button
                        onClick={() => setShowLayers(!showLayers)}
                        className="w-full px-4 py-3 flex items-center justify-between font-semibold text-sm text-white bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 transition-all"
                    >
                        <span className="flex items-center gap-2">
                            <LayersIcon />
                            Capas
                        </span>
                        <span className={`transform transition-transform ${showLayers ? 'rotate-180' : ''}`}>▼</span>
                    </button>
                    {showLayers && (
                        <div className="p-3 space-y-3">
                            {/* Capas propias */}
                            <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                                <h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Entidades</h5>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={showSedes}
                                        onChange={(e) => setShowSedes(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">📍 Sedes</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={showParcelas}
                                        onChange={(e) => setShowParcelas(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🗺️ Parcelas</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={showMunicipios}
                                        onChange={(e) => setShowMunicipios(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🧭 Límites partidos</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={showArba}
                                        onChange={(e) => setShowArba(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🏛️ ARBA Parcelario</span>
                                </label>
                            </div>

                            {/* Capa OSM (admin, provincial especial o municipal) */}
                            {canViewOsmStaging && (
                                <div className="border border-orange-200 rounded-lg p-2 bg-orange-50">
                                    <h5 className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Datos abiertos (OSM)</h5>
                                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={showOsmStaging}
                                            onChange={(e) => setShowOsmStaging(e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm text-gray-700">🟠 Entidades OSM</span>
                                    </label>
                                    <p className="text-[11px] text-gray-500 mt-1 pl-6">
                                        Naranja: pendiente · Rojo: rechazado · Verde: validado.
                                    </p>
                                </div>
                            )}
                            
                            {/* Capas base */}
                            <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                                <h5 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Mapa Base</h5>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="osm"
                                        checked={baseLayer === 'osm'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🗺️ OpenStreetMap</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="satellite"
                                        checked={baseLayer === 'satellite'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🛰️ Satélite</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="topo"
                                        checked={baseLayer === 'topo'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">⛰️ Topográfico</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="ign"
                                        checked={baseLayer === 'ign'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🇦🇷 IGN Argentina</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="positron"
                                        checked={baseLayer === 'positron'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">⚪ CartoDB Positron</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="voyager"
                                        checked={baseLayer === 'voyager'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">🏢 CartoDB Voyager</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                                    <input
                                        type="radio"
                                        name="baseLayer"
                                        value="dark"
                                        checked={baseLayer === 'dark'}
                                        onChange={(e) => setBaseLayer(e.target.value)}
                                        className="w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">⚫ CartoDB Dark</span>
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Panel de información toggleable - Abajo a la derecha */}
            {showInfo && (
                <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-xl p-4 z-10 w-80 max-h-48 overflow-y-auto">
                    <h4 className="text-sm font-bold text-gray-700 mb-2">Información de Capas</h4>
                    {selectedPartido ? (
                        <>
                            <p className="text-xs text-gray-600">
                                Sedes georreferenciadas en partido: <span className="font-semibold text-blue-600">{filteredCount}</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                Total disponible: {entidades.length}
                            </p>
                        </>
                    ) : (
                        <p className="text-xs text-gray-600">
                            Total de entidades: {entidades.length}
                        </p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                        Haz clic en un punto para ver detalles
                    </p>
                </div>
            )}
            
            {/* Hover preview tooltip */}
            {hoveredEntity && (
                <div 
                    className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50 pointer-events-none"
                    style={{
                        left: mousePosition.x + 15,
                        top: mousePosition.y - 10,
                        transform: 'translateY(-100%)'
                    }}
                >
                    <p className="font-semibold text-sm text-gray-800">{hoveredEntity.nombre_completo}</p>
                    <p className="text-xs text-gray-500 capitalize">{hoveredEntity.tipo_entidad || 'Entidad'}</p>
                    {hoveredEntity.id_red && (
                        <p className="text-xs text-gray-400 mt-1">ID: {hoveredEntity.id_red}</p>
                    )}
                </div>
            )}

            {/* Panel de validación de entidad OSM */}
            {osmSeleccionada && (
                <div className={`absolute bottom-4 right-4 md:w-96 bg-white rounded-xl shadow-xl border p-4 z-20 max-h-[70vh] overflow-y-auto ${
                    osmSeleccionada.estado === 'importado' ? 'border-green-200'
                        : osmSeleccionada.estado === 'rechazado' ? 'border-red-200'
                            : 'border-orange-200'
                }`}>
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full mb-1 ${
                                osmSeleccionada.estado === 'importado' ? 'bg-green-100 text-green-700'
                                    : osmSeleccionada.estado === 'rechazado' ? 'bg-red-100 text-red-700'
                                        : 'bg-orange-100 text-orange-700'
                            }`}>
                                OSM · {osmSeleccionada.estado === 'importado' ? 'VALIDADO'
                                    : osmSeleccionada.estado === 'rechazado' ? 'RECHAZADO'
                                        : 'PENDIENTE'}
                            </span>
                            <h3 className="text-lg font-bold text-gray-900">
                                {osmSeleccionada.nombre_completo || 'Sin nombre'}
                            </h3>
                            <p className="text-xs text-gray-500">{osmSeleccionada.osm_id}</p>
                            <button
                                onClick={() => zoomToOsmSeleccionada()}
                                disabled={!osmSeleccionadaCoords}
                                title={osmSeleccionadaCoords ? 'Centrar en el punto' : 'Sin coordenadas'}
                                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                🔍 Zoom al punto
                            </button>
                        </div>
                        <button
                            onClick={() => { setOsmSeleccionada(null); setOsmRejectMode(false); setOsmObservaciones(''); }}
                            className="text-gray-400 hover:text-gray-600 ml-2"
                        >
                            ✕
                        </button>
                    </div>

                    {osmSeleccionada.estado === 'pendiente' && (
                        <div className="space-y-2 mb-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del club</label>
                                <input
                                    type="text"
                                    value={osmNombreEditado}
                                    onChange={(e) => setOsmNombreEditado(e.target.value)}
                                    placeholder="Nombre de la entidad"
                                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Domicilio</label>
                                <input
                                    type="text"
                                    value={osmDomicilioEditado}
                                    onChange={(e) => setOsmDomicilioEditado(e.target.value)}
                                    placeholder="Domicilio"
                                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                />
                            </div>
                            <p className="text-[11px] text-gray-500">Podés corregir estos datos antes de importar.</p>
                        </div>
                    )}

                    <div className="space-y-1.5 text-sm mb-3">
                        <OsmRow label="Partido" value={osmSeleccionada.partido_nombre || osmSeleccionada.codigo_partido} />
                        <OsmRow label="Municipio" value={osmSeleccionada.municipio} />
                        <OsmRow label="Localidad" value={osmSeleccionada.localidad} />
                        {osmSeleccionada.estado !== 'pendiente' && (
                            <OsmRow label="Domicilio" value={osmSeleccionada.domicilio} />
                        )}
                        <OsmRow label="Teléfono" value={osmSeleccionada.telefono} />
                        <OsmRow label="Email" value={osmSeleccionada.email} />
                        <OsmRow label="Web" value={osmSeleccionada.web} />
                        <OsmRow label="Deportes" value={osmSeleccionada.deportes} />
                        <OsmRow label="Tipo OSM" value={[osmSeleccionada.leisure, osmSeleccionada.amenity].filter(Boolean).join(' / ')} />
                    </div>

                    {osmAccionError && (
                        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            {osmAccionError}
                        </div>
                    )}
                    {osmAccionMsg && (
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                            {osmAccionMsg}
                        </div>
                    )}

                    {osmSeleccionada.estado === 'pendiente' ? (
                        osmRejectMode ? (
                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-gray-600">
                                    Motivo del rechazo <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={osmObservaciones}
                                    onChange={(e) => setOsmObservaciones(e.target.value)}
                                    rows={3}
                                    placeholder="Indicá por qué se rechaza este registro..."
                                    className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { setOsmRejectMode(false); setOsmObservaciones(''); setOsmAccionError(null); }}
                                        disabled={osmAccionLoading}
                                        className="flex-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={() => ejecutarAccionOsm('reject', osmObservaciones)}
                                        disabled={osmAccionLoading || !osmObservaciones.trim()}
                                        className="flex-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
                                    >
                                        {osmAccionLoading ? 'Rechazando...' : 'Confirmar rechazo'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => ejecutarAccionOsm('import')}
                                    disabled={osmAccionLoading}
                                    className="flex-1 px-3 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
                                >
                                    {osmAccionLoading ? 'Procesando...' : 'Importar a RED'}
                                </button>
                                <button
                                    onClick={() => { setOsmRejectMode(true); setOsmAccionError(null); setOsmObservaciones(''); }}
                                    disabled={osmAccionLoading}
                                    className="flex-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors"
                                >
                                    Rechazar
                                </button>
                            </div>
                        )
                    ) : (
                        <div className="text-xs font-semibold text-gray-500">
                            Estado: <span className="capitalize">{osmSeleccionada.estado}</span>
                            {osmSeleccionada.entidad_id_red ? ` · ID ${osmSeleccionada.entidad_id_red}` : ''}
                            {osmSeleccionada.estado === 'rechazado' && osmSeleccionada.observaciones && (
                                <p className="mt-1 font-normal italic text-gray-600">Motivo: {osmSeleccionada.observaciones}</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Panel de información de entidad seleccionada - Solo cuando showInfo está activo */}
            {showInfo && entidadSeleccionada && (
                <div className={`absolute bottom-4 right-4 ${isPrivate ? 'md:w-[480px]' : 'md:w-80'} bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-10 max-h-[500px] overflow-y-auto`}>
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {entidadSeleccionada.nombre_completo}
                            </h3>
                            {entidadSeleccionada.nombre_fantasia && (
                                <p className="text-sm text-gray-500 italic">{entidadSeleccionada.nombre_fantasia}</p>
                            )}
                        </div>
                        <button
                            onClick={() => setEntidadSeleccionada(null)}
                            className="text-gray-400 hover:text-gray-600 ml-2"
                        >
                            ✕
                        </button>
                    </div>
                    
                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 mb-3 capitalize">
                        {entidadSeleccionada.tipo_entidad || 'Entidad'}
                    </span>
                    
                    {isPrivate ? (
                        <>
                            {/* Indicador de carga */}
                            {loadingDetalle && (
                                <div className="flex items-center justify-center p-4">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                    <span className="ml-2 text-gray-500 text-xs">Cargando detalles...</span>
                                </div>
                            )}

                            {/* Panel con Tabs */}
                            {!loadingDetalle && entidadDetalle && (
                                <EntidadDetailTabs data={entidadDetalle} entidadSeleccionada={entidadSeleccionada} />
                            )}
                        </>
                    ) : (
                        <div className="space-y-2 text-sm">
                            {entidadSeleccionada.id_red && (
                                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs">ID RED</span>
                                    <span className="font-medium text-gray-700">{entidadSeleccionada.id_red}</span>
                                </div>
                            )}
                            {entidadSeleccionada.tipo_entidad && (
                                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs">TIPO</span>
                                    <span className="font-medium text-gray-700 capitalize">{entidadSeleccionada.tipo_entidad}</span>
                                </div>
                            )}
                            {entidadSeleccionada.municipio && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block mb-1">UBICACIÓN</span>
                                    <span className="font-medium text-gray-700">{entidadSeleccionada.municipio}</span>
                                    {entidadSeleccionada.codigo_partido && (
                                        <span className="text-gray-500 text-xs block">Partido {entidadSeleccionada.codigo_partido}</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            
            {/* Leyenda - Solo visible cuando hay sedes Y está en modo byType */}
            {showSedes && styleMode === "byType" && (
                <div className="absolute bottom-6 left-4 bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-10">
                    <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Tipo de Entidad</h4>
                    <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            <span className="text-gray-700">Asociación Civil</span>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-green-500"></span>
                            <span className="text-gray-700">Fundación</span>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                            <span className="text-gray-700">Simple Asociación</span>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                            <span className="text-gray-700">Otro</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Leyenda - Solo visible cuando hay sedes Y está en modo byEditor (Privado) */}
            {isPrivate && showSedes && styleMode === "byEditor" && (
                <div className="absolute bottom-6 left-4 bg-white rounded-xl shadow-xl border border-slate-200 p-3 z-10">
                    <h4 className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Editor Asignado</h4>
                    <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                            <span className="text-gray-700">Con editor asignado</span>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            <span className="text-gray-700">Sin editor asignado</span>
                        </div>
                    </div>
                </div>
            )}
            {/* Leyenda OSM - Estado de validación */}
            {canViewOsmStaging && showOsmStaging && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-xl border border-slate-200 px-3 py-2 z-10">
                    <h4 className="text-[10px] font-bold text-gray-700 mb-1 uppercase tracking-wide text-center">Entidades OSM</h4>
                    <div className="flex items-center gap-3 text-xs">
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                            <span className="text-gray-700">Pendiente</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-red-500"></span>
                            <span className="text-gray-700">Rechazado</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-green-500"></span>
                            <span className="text-gray-700">Validado</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
const OsmRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex items-start gap-2 p-1.5 bg-gray-50 rounded">
            <span className="text-gray-400 text-xs uppercase w-24 shrink-0">{label}</span>
            <span className="font-medium text-gray-700 break-words">{value}</span>
        </div>
    );
};

const EntidadDetailTabs = ({ data, entidadSeleccionada }) => {
    const [activeTab, setActiveTab] = useState('identidad');

    // Helper para verificar si un valor es válido
    const hasValue = (val) => {
        if (val === null || val === undefined) return false;
        if (val === false) return false;
        if (typeof val === 'string' && val.trim() === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        if (typeof val === 'object' && Object.keys(val).length === 0) return false;
        if (typeof val === 'number' && val === 0) return false;
        return true;
    };

    // Verificar si un objeto tiene algún campo con valor
    const hasAnyValue = (obj) => {
        if (!obj || typeof obj !== 'object') return false;
        return Object.values(obj).some(hasValue);
    };

    const SinInformacion = () => (
        <div className="flex items-center justify-center p-6 text-gray-400 text-sm italic">
            Sin información
        </div>
    );

    const tabs = [
        { id: 'identidad', label: 'Identidad', icon: '🏢' },
        { id: 'legal', label: 'Legal Social', icon: '📋' },
        { id: 'infra', label: 'Infraestructura', icon: '🏗️' },
        { id: 'predio', label: 'Predio', icon: '🌱' },
        { id: 'deporte', label: 'Vida Deportiva', icon: '⚽' },
        { id: 'diag', label: 'Asistencia y Necesidades', icon: '📊' }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'identidad':
                {
                    const hasIdentidadData = hasAnyValue(data.identidad) || entidadSeleccionada.id_red || entidadSeleccionada.municipio;
                    if (!hasIdentidadData) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            {entidadSeleccionada.id_red && (
                                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs">ID RED</span>
                                    <span className="font-medium text-gray-700">{entidadSeleccionada.id_red}</span>
                                </div>
                            )}
                            {data.identidad?.fecha_fundacion && (
                                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs">FUNDACIÓN</span>
                                    <span className="font-medium text-gray-700">{new Date(data.identidad.fecha_fundacion).toLocaleDateString('es-AR')}</span>
                                </div>
                            )}
                            {entidadSeleccionada.municipio && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block mb-1">UBICACIÓN</span>
                                    <span className="font-medium text-gray-700">{entidadSeleccionada.municipio}</span>
                                    {entidadSeleccionada.codigo_partido && (
                                        <span className="text-gray-500 text-xs block">Partido {entidadSeleccionada.codigo_partido}</span>
                                    )}
                                </div>
                            )}
                            {hasValue(data.identidad?.email) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">EMAIL</span>
                                    <span className="font-medium text-xs break-all">{data.identidad.email}</span>
                                </div>
                            )}
                            {hasValue(data.identidad?.telefono) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">TELÉFONO</span>
                                    <span className="font-medium text-xs">{data.identidad.telefono}</span>
                                </div>
                            )}
                            {hasValue(data.identidad?.domicilio_legal) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">DOMICILIO LEGAL</span>
                                    <span className="font-medium text-xs">{data.identidad.domicilio_legal}</span>
                                </div>
                            )}
                            {hasValue(data.identidad?.domicilio_deportivo) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">DOMICILIO DEPORTIVO</span>
                                    <span className="font-medium text-xs">{data.identidad.domicilio_deportivo}</span>
                                </div>
                            )}
                            {data.identidad?.web && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">WEB</span>
                                    <a href={data.identidad.web} target="_blank" rel="noopener noreferrer" className="font-medium text-xs text-blue-600 hover:underline break-all">
                                        {data.identidad.web}
                                    </a>
                                </div>
                            )}
                            {hasValue(data.identidad?.redes_sociales) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Redes sociales</span>
                                    <span className="font-medium text-xs break-all">{data.identidad.redes_sociales}</span>
                                </div>
                            )}
                        </div>
                    );
                }

            case 'legal':
                {
                    if (!hasAnyValue(data.legalSocial)) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            {hasValue(data.legalSocial?.personeria_vigente) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Personería</span>
                                    <span className="font-medium text-xs">{data.legalSocial.personeria_vigente}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.denominacion_registrada) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Denominación</span>
                                    <span className="font-medium text-xs">{data.legalSocial.denominacion_registrada}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.nro_matricula) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Matrícula</span>
                                    <span className="font-medium text-xs">{data.legalSocial.nro_matricula}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.socios_activos) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Socios activos</span>
                                    <span className="font-medium text-xs">{data.legalSocial.socios_activos}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.socios_vitalicios) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Socios vitalicios</span>
                                    <span className="font-medium text-xs">{data.legalSocial.socios_vitalicios}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.presi_nombre) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Presidente</span>
                                    <span className="font-medium text-xs">{data.legalSocial.presi_nombre}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.secre_nombre) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Secretario</span>
                                    <span className="font-medium text-xs">{data.legalSocial.secre_nombre}</span>
                                </div>
                            )}
                            {hasValue(data.legalSocial?.teso_nombre) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Tesorero</span>
                                    <span className="font-medium text-xs">{data.legalSocial.teso_nombre}</span>
                                </div>
                            )}
                        </div>
                    );
                }

            case 'infra':
                {
                    if (!hasAnyValue(data.infra)) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            <div className="border-b border-gray-200 pb-2 mb-2">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Instalaciones</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {data.infra?.canchaFutbol && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Fútbol</span>}
                                    {data.infra?.canchaFutbol_5_7 && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Fútbol 5/7</span>}
                                    {data.infra?.canchaBasket && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Básquet</span>}
                                    {data.infra?.canchaVoley && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Vóley</span>}
                                    {data.infra?.canchaHockey && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Hockey</span>}
                                    {data.infra?.canchasTenis && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Tenis</span>}
                                    {data.infra?.gimnasio && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Gimnasio</span>}
                                    {data.infra?.natatorio && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Natatorio</span>}
                                    {data.infra?.pistaAtletismo && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Atletismo</span>}
                                    {data.infra?.pistaPatin && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Patín</span>}
                                    {data.infra?.salonArtesMarciales && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Artes Marciales</span>}
                                    {data.infra?.posee_sum && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">SUM</span>}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {hasValue(data.infra?.srv_energia) && (
                                    <div className="p-2 bg-gray-50 rounded">
                                        <span className="text-gray-400 text-xs block">Energía</span>
                                        <span className="font-medium text-xs">{data.infra.srv_energia}</span>
                                    </div>
                                )}
                                {hasValue(data.infra?.srv_agua) && (
                                    <div className="p-2 bg-gray-50 rounded">
                                        <span className="text-gray-400 text-xs block">Agua</span>
                                        <span className="font-medium text-xs">{data.infra.srv_agua}</span>
                                    </div>
                                )}
                                {hasValue(data.infra?.srv_gas) && (
                                    <div className="p-2 bg-gray-50 rounded">
                                        <span className="text-gray-400 text-xs block">Gas</span>
                                        <span className="font-medium text-xs">{data.infra.srv_gas}</span>
                                    </div>
                                )}
                                {hasValue(data.infra?.srv_cloacas) && (
                                    <div className="p-2 bg-gray-50 rounded">
                                        <span className="text-gray-400 text-xs block">Cloacas</span>
                                        <span className="font-medium text-xs">{data.infra.srv_cloacas}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {hasValue(data.infra?.cant_vestuarios) && (
                                    <div className="flex-1 p-2 bg-gray-50 rounded text-center">
                                        <span className="text-gray-400 text-xs block">Vestuarios</span>
                                        <span className="font-medium text-sm">{data.infra.cant_vestuarios}</span>
                                    </div>
                                )}
                                {hasValue(data.infra?.cant_banos) && (
                                    <div className="flex-1 p-2 bg-gray-50 rounded text-center">
                                        <span className="text-gray-400 text-xs block">Baños</span>
                                        <span className="font-medium text-sm">{data.infra.cant_banos}</span>
                                    </div>
                                )}
                            </div>
                            {hasValue(data.infra?.estadoGeneral) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Estado edilicio</span>
                                    <span className="font-medium text-xs">{data.infra.estadoGeneral}</span>
                                </div>
                            )}
                        </div>
                    );
                }

            case 'predio':
                {
                    if (!hasAnyValue(data.predio)) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            {hasValue(data.predio?.cuenta_con_predio) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Tiene predio</span>
                                    <span className="font-medium text-xs">{data.predio.cuenta_con_predio}</span>
                                </div>
                            )}
                            {hasValue(data.predio?.tipo_dominio) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Tipo de dominio</span>
                                    <span className="font-medium text-xs">{data.predio.tipo_dominio}</span>
                                </div>
                            )}
                            {hasValue(data.predio?.superficie_aprox) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Superficie</span>
                                    <span className="font-medium text-xs">{data.predio.superficie_aprox} m²</span>
                                </div>
                            )}
                            {(hasValue(data.predio?.catastro_circ) || hasValue(data.predio?.catastro_secc) || hasValue(data.predio?.catastro_manz) || hasValue(data.predio?.catastro_parc)) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Partida catastral</span>
                                    <span className="font-medium text-xs">
                                        {data.predio.catastro_circ && `Circ: ${data.predio.catastro_circ} `}
                                        {data.predio.catastro_secc && `Sec: ${data.predio.catastro_secc} `}
                                        {data.predio.catastro_manz && `Mz: ${data.predio.catastro_manz} `}
                                        {data.predio.catastro_parc && `Pc: ${data.predio.catastro_parc}`}
                                    </span>
                                </div>
                            )}
                            {data.predio?.posee_escritura && (
                                <div className="flex justify-between p-2 bg-green-50 rounded">
                                    <span className="text-gray-500 text-xs">Posee escritura</span>
                                    <span className="font-medium text-xs text-green-600">✓</span>
                                </div>
                            )}
                            {hasValue(data.predio?.titular_registral) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Titular registral</span>
                                    <span className="font-medium text-xs">{data.predio.titular_registral}</span>
                                </div>
                            )}
                        </div>
                    );
                }

            case 'deporte':
                {
                    const hasDeporteData = hasAnyValue(data.vidaDeportiva) || (data.vidaDeportiva?.disciplinas?.length > 0);
                    if (!hasDeporteData) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            {data.vidaDeportiva?.disciplinas?.length > 0 && (
                                <div>
                                    <h5 className="text-xs font-bold text-gray-600 mb-2">Disciplinas</h5>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {data.vidaDeportiva.disciplinas.map((disc, idx) => (
                                            <div key={idx} className="p-2 bg-blue-50 rounded text-xs">
                                                <div className="font-medium text-blue-900">{disc.nombre_disciplina}</div>
                                                <div className="text-blue-600 mt-1">
                                                    {disc.cantidad} deportistas • {disc.categorias}
                                                    {disc.es_federado && <span className="ml-1 font-semibold">• Federado</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {data.vidaDeportiva?.deporte_adaptado && (
                                <div className="flex justify-between p-2 bg-green-50 rounded">
                                    <span className="text-gray-500 text-xs">Deporte adaptado</span>
                                    <span className="font-medium text-xs text-green-600">✓</span>
                                </div>
                            )}
                            {hasValue(data.vidaDeportiva?.adaptado_cant) && (
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-gray-500 text-xs">Deportistas adaptados</span>
                                    <span className="font-medium text-xs">{data.vidaDeportiva.adaptado_cant}</span>
                                </div>
                            )}
                            {data.vidaDeportiva?.gen_equipos_fem && (
                                <div className="flex justify-between p-2 bg-green-50 rounded">
                                    <span className="text-gray-500 text-xs">Equipos femeninos</span>
                                    <span className="font-medium text-xs text-green-600">✓</span>
                                </div>
                            )}
                        </div>
                    );
                }

            case 'diag':
                {
                    if (!hasAnyValue(data.diagnostico)) return <SinInformacion />;

                    return (
                        <div className="space-y-2">
                            {hasValue(data.diagnostico?.fortalezas) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Fortalezas</span>
                                    <span className="font-medium text-xs">{data.diagnostico.fortalezas}</span>
                                </div>
                            )}
                            {hasValue(data.diagnostico?.debilidades) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Debilidades</span>
                                    <span className="font-medium text-xs">{data.diagnostico.debilidades}</span>
                                </div>
                            )}
                            {hasValue(data.diagnostico?.proyectos_corto_plazo) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Proyectos</span>
                                    <span className="font-medium text-xs">{data.diagnostico.proyectos_corto_plazo}</span>
                                </div>
                            )}
                            {data.diagnostico?.necesita_asistencia_tecnica && (
                                <div className="flex justify-between p-2 bg-amber-50 rounded">
                                    <span className="text-gray-500 text-xs">Necesita asistencia</span>
                                    <span className="font-medium text-xs text-amber-600">Sí</span>
                                </div>
                            )}
                            {hasValue(data.diagnostico?.observaciones_finales) && (
                                <div className="p-2 bg-gray-50 rounded">
                                    <span className="text-gray-400 text-xs block">Observaciones</span>
                                    <span className="font-medium text-xs">{data.diagnostico.observaciones_finales}</span>
                                </div>
                            )}
                            {(data.diagnostico?.nec_infra || data.diagnostico?.nec_dominio || data.diagnostico?.nec_equip || data.diagnostico?.nec_iluminacion || data.diagnostico?.nec_edilicia) && (
                                <div className="border-t border-gray-200 pt-2 mt-2">
                                    <span className="text-gray-400 text-xs block mb-1">Necesidades identificadas</span>
                                    <div className="flex flex-wrap gap-1">
                                        {data.diagnostico?.nec_infra && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Infraestructura</span>}
                                        {data.diagnostico?.nec_dominio && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Dominio</span>}
                                        {data.diagnostico?.nec_equip && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Equipamiento</span>}
                                        {data.diagnostico?.nec_iluminacion && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Iluminación</span>}
                                        {data.diagnostico?.nec_edilicia && <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Edilicia</span>}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }

            default:
                return <SinInformacion />;
        }
    };

    return (
        <div className="mt-3">
            <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200 pb-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            activeTab === tab.id
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="max-h-64 overflow-y-auto">
                {renderTabContent()}
            </div>
        </div>
    );
};
